import { UIConfigurationProvider } from "@/contexts/UIConfiguration";
import { embeddedScaffold, toEmbeddedEnrollmentScaffold } from "@/scaffold";
import { Configuration } from "@/services/apiTypes";
import { useLogout } from "@/utils/logout";
import { useAnalytics, useAnalyticsSearchParams } from "@Light/app/analytics";
import { Feature } from "@Light/app/features";
import { DocumentsRoute } from "@Light/components/account/Documents/DocumentsRoute";
import { PaymentMethodUpdated } from "@Light/components/account/PaymentMethod/PaymentMethodUpdated";
import { UpdatePaymentMethod } from "@Light/components/account/PaymentMethod/UpdatePaymentMethod";
import { BillingInvoiceRoute } from "@Light/components/dash/BillingInvoice/BillingInvoiceRoute";
import { BillingRoute } from "@Light/components/dash/BillingRoute";
import { ConnectBatteryRoutes } from "@Light/components/devices/ConnectBattery";
import { Enroll } from "@Light/components/enroll";
import { OptOutSurveyProvider } from "@Light/components/optOutSurvey";
import type { Account } from "@Light/components/page/account";
import { WithAccount } from "@Light/components/page/account";
import { WithHeader } from "@Light/components/page/header";
import { Overlay } from "@Light/components/page/Overlay";
import { Renew } from "@Light/components/renew/Renew";
import { useScaffold } from "@Light/scaffold";
import { Provider as LightProvider } from "@Light/scaffold/Provider";
import { FlowScope } from "@Light/services/flowScope";
import { requiredFlowScopeEntryPath } from "@Light/services/flowScopeRoutes";
import { useLight } from "@Light/services/light";
import { OptOutFilter } from "@Light/services/lightTypes";
import { WithLocationPlan } from "@Light/utils/plan";
import * as Sentry from "@sentry/react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Close } from "./Close";
import { EnrollmentNotComplete } from "./EnrollmentNotComplete";
import { Login } from "./Login";
import { ServiceAlreadyActive } from "./ServiceAlreadyActive";
import { SessionExpired } from "./SessionExpired";
import {
  WithConfiguration,
  useConfiguration,
  useRequireConfiguration,
} from "./WithConfiguration";

export const AppRoutes = () => {
  useAnalyticsSearchParams();

  return (
    <UIConfigurationProvider>
      <LightProvider scaffold={embeddedScaffold}>
        <Overlay>
          <Sentry.ErrorBoundary
            fallback={<SessionExpired title="Unexpected error" />}
          >
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/session-expired" element={<SessionExpired />} />
              <Route
                path="/*"
                element={
                  <WithConfiguration>
                    <ConfigurationRoutes />
                  </WithConfiguration>
                }
              />
            </Routes>
          </Sentry.ErrorBoundary>
        </Overlay>
      </LightProvider>
    </UIConfigurationProvider>
  );
};

export const ConfigurationRoutes = () => {
  const configuration = useConfiguration();
  if (!configuration) {
    return (
      <Routes>
        <Route path="/*" element={<Navigate to="/session-expired" />} />
      </Routes>
    );
  }

  return (
    <WithAccount>
      <LoggedInRoutes />
    </WithAccount>
  );
};

export type LoggedInRoutesProps = {};

export const LoggedInRoutes = () => {
  const configuration = useRequireConfiguration();
  const { useGetAccountQuery } = useLight();
  const account = useGetAccountQuery();
  const { planUUID } = useAnalytics();

  const features = configuration.feature_flags ?? [];

  return (
    <Feature.Provider features={features}>
      <ScopedRoutes
        configuration={configuration}
        account={account.data}
        planUUID={planUUID}
      />
    </Feature.Provider>
  );
};

export type ScopedRoutesProps = {
  configuration: Configuration;
  account?: Account;
  planUUID?: string;
};

export const ScopedRoutes = ({
  configuration,
  account,
  planUUID,
}: ScopedRoutesProps) => {
  const logout = useLogout();
  if (configuration.scope === FlowScope.Enrollment) {
    return (
      <WithEmbeddedEnrollmentScaffold>
        <Routes>
          <Route
            path="/service-already-active"
            element={<ServiceAlreadyActive />}
          />
          <Route
            path="/enroll/*"
            element={
              <Enroll
                serviceActivePath="/service-already-active"
                planUUID={planUUID}
                onLogout={logout}
              />
            }
          />
          <Route
            path="/"
            element={
              <Navigate to={requiredFlowScopeEntryPath(FlowScope.Enrollment)} />
            }
          />
          <Route path="/*" element={<Close />} />
        </Routes>
      </WithEmbeddedEnrollmentScaffold>
    );
  }

  if (configuration.scope === FlowScope.ConnectBattery) {
    return (
      <Routes>
        <Route
          path="/*"
          element={
            <WithHeader>
              <ConnectBatteryRoutes
                onComplete={logout}
                closeElement={<Close />}
              />
            </WithHeader>
          }
        />
      </Routes>
    );
  }

  if (!account?.enrollment.is_enrollment_finalized) {
    return <EnrollmentNotComplete />;
  }

  return (
    <WithLocationPlan>
      <ScopedRoutesWithEnrollment />
    </WithLocationPlan>
  );
};

export type WithEmbeddedEnrollmentScaffoldProps = {
  children: React.ReactNode;
};

export const WithEmbeddedEnrollmentScaffold = ({
  children,
}: WithEmbeddedEnrollmentScaffoldProps) => {
  const scaffold = useScaffold();
  return (
    <LightProvider scaffold={toEmbeddedEnrollmentScaffold(scaffold)}>
      {children}
    </LightProvider>
  );
};

export type ScopedRoutesWithEnrollmentProps = {};

export const ScopedRoutesWithEnrollment = () => {
  const configuration = useRequireConfiguration();
  const logout = useLogout();
  if (configuration.scope === FlowScope.UpdatePaymentMethod) {
    return (
      <Routes>
        <Route
          path="/account/payment-method/update"
          element={
            <WithHeader>
              <UpdatePaymentMethod hideBack />
            </WithHeader>
          }
        />
        <Route
          path="/account/payment-method/payment-method-updated"
          element={
            <WithHeader>
              <PaymentMethodUpdated hideBack />
            </WithHeader>
          }
        />
        <Route
          path="/dash/billing/invoice/:invoiceNumber/*"
          element={
            <WithHeader>
              <BillingInvoiceRoute
                payInvoiceOnly
                hideBack
                redirect={<Close />}
              />
            </WithHeader>
          }
        />
        <Route
          path="/"
          element={
            <Navigate
              to={requiredFlowScopeEntryPath(FlowScope.UpdatePaymentMethod)}
            />
          }
        />
        <Route path="/*" element={<Close />} />
      </Routes>
    );
  }

  if (configuration.scope === FlowScope.Documents) {
    return (
      <Routes>
        <Route
          path="/account/documents/*"
          element={
            <WithHeader>
              <DocumentsRoute hideRootBack />
            </WithHeader>
          }
        />
        <Route
          path="/"
          element={
            <Navigate to={requiredFlowScopeEntryPath(FlowScope.Documents)} />
          }
        />
        <Route path="/*" element={<Close />} />
      </Routes>
    );
  }

  if (configuration.scope === FlowScope.Billing) {
    return (
      <Routes>
        <Route
          path="/account/payment-method/update"
          element={
            <WithHeader>
              <UpdatePaymentMethod
                backLink={requiredFlowScopeEntryPath(FlowScope.Billing)}
              />
            </WithHeader>
          }
        />
        <Route
          path="/account/payment-method/payment-method-updated"
          element={
            <WithHeader>
              <PaymentMethodUpdated
                backLink={requiredFlowScopeEntryPath(FlowScope.Billing)}
                returnLink={requiredFlowScopeEntryPath(FlowScope.Billing)}
              />
            </WithHeader>
          }
        />
        <Route
          path="/dash/billing/*"
          element={
            <WithHeader>
              <BillingRoute hideRootBack showDivider />
            </WithHeader>
          }
        />
        <Route
          path="/"
          element={
            <Navigate to={requiredFlowScopeEntryPath(FlowScope.Billing)} />
          }
        />
        <Route path="/*" element={<Close />} />
      </Routes>
    );
  }

  if (configuration.scope === FlowScope.Renewal) {
    return (
      <Routes>
        <Route
          path="/renew/*"
          element={
            <OptOutSurveyProvider
              filter={OptOutFilter.Renewal}
              onConfirmClose={(source) => {
                logout(source ?? "opt_out_survey", true);
              }}
            >
              <WithHeader>
                <Renew onLogout={logout} />
              </WithHeader>
            </OptOutSurveyProvider>
          }
        />
        <Route
          path="/"
          element={
            <Navigate to={requiredFlowScopeEntryPath(FlowScope.Renewal)} />
          }
        />
        <Route path="/*" element={<Close />} />
      </Routes>
    );
  }

  throw new Error(`Invalid scope: ${configuration.scope}`);
};
